'use client';

import { Mail, MapPin, Clock, Github, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ContactSection } from '@/app/components/home/contact-section';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';
import { useState } from 'react';

const FAQ_ITEMS = [
  {
    q: 'How do you price projects?',
    a: "We scope work in phases and price per phase — typically fixed-fee for defined scope, or time-and-materials for ongoing product development. We'll give you a clear estimate before any commitment.",
  },
  {
    q: 'Do you work outside Ireland?',
    a: "Our focus is Irish community organisations, but we're open to remote partnerships across the EU. If your mission aligns with ours, let's talk.",
  },
  {
    q: 'What does ongoing support look like?',
    a: 'We operate what we build. That means monitoring, incident response, regular updates, and a monthly check-in. No separate "maintenance contract" — it\'s baked into how we work.',
  },
  {
    q: 'Can SpaceNest integrate with our existing tools?',
    a: 'SpaceNest has a REST API and webhook system. We integrate with Stripe for payments, and can connect to your existing member management or accounting tools on request.',
  },
] as const;

function AccordionItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="border-b border-sandron-ink-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-5 text-left focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary"
      >
        <span className="font-display text-lg font-bold text-sandron-ink-900 pr-4">
          {question}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-sandron-ink-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0 : 0.25,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-base leading-[1.6] text-sandron-ink-700">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ContactPage() {
  const { ref: faqRef, isVisible: faqVisible } = useScrollReveal();
  const prefersReducedMotion = useReducedMotion();

  return (
    <>
      {/* Page hero */}
      <section className="pb-12 pt-[clamp(96px,12vw,160px)]">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
          <div className="flex items-center gap-3">
            <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
              Contact
            </span>
          </div>
          <h1 className="mt-6 font-display text-[clamp(40px,5vw,72px)] font-extrabold leading-[1.04] tracking-[-0.025em] text-sandron-ink-900">
            Let&apos;s talk.
          </h1>
          <p className="mt-5 max-w-[600px] text-[clamp(17px,1.4vw,20px)] leading-[1.6] text-sandron-ink-700">
            Whether you run a community centre, want to partner on a product, or
            are curious about working together — we&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact form + info (reuses homepage component) */}
      <ContactSection />

      {/* FAQ */}
      <section className="bg-sandron-ink-50 py-[clamp(80px,10vw,120px)]">
        <div ref={faqRef} className="mx-auto max-w-[720px] px-5 md:px-10 lg:px-0">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 16 }}
            animate={faqVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0.15 : 0.6,
              ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            }}
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
                FAQ
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(32px,4vw,48px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900">
              Common questions.
            </h2>
          </motion.div>

          <div className="mt-10">
            {FAQ_ITEMS.map((item) => (
              <AccordionItem key={item.q} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
